<template>
  <div class="external-onboarding">
    <v-container class="fill-height pa-0" fluid>
      <v-layout align-center justify-center fill-height>
        <v-flex xs12 sm10 md8 lg6 xl5>
          <v-card class="elevation-4">
            <!-- Header -->
            <v-toolbar flat color="primary" dark class="rounded-top">
              <v-icon left large>local_shipping</v-icon>
              <v-toolbar-title class="headline font-weight-bold">
                Onboarding de Embarque
              </v-toolbar-title>
              <v-spacer></v-spacer>
            </v-toolbar>

            <!-- Barra de Progreso -->
            <div class="progress-section pa-4 border-bottom">
              <div class="progress-bar mb-2">
                <div class="progress-fill" :style="{ width: progressPercentage + '%' }"></div>
              </div>
              <p class="caption text-center mb-0">{{ progressText }}</p>
            </div>

            <!-- Contenido Principal -->
            <v-card-text class="pa-6">
              <!-- Error: Link no válido o ya utilizado -->
              <div v-if="showError && !shipmentInfo" class="text-center py-8">
                <v-icon size="80" color="error" class="mb-4">error_outline</v-icon>
                <h2 class="headline font-weight-bold mb-4">{{ errorMessage }}</h2>
                <p class="body-1 text-muted mb-6">
                  Si crees que esto es un error, solicita un nuevo link al administrador que te invitó.
                </p>
                <v-btn color="primary" outlined href="/">
                  <v-icon left>home</v-icon>
                  Volver al Inicio
                </v-btn>
              </div>

              <!-- Paso 1: Formulario -->
              <div v-if="currentStep === 1 && shipmentInfo" class="step-container">
                <div class="step-title mb-6">
                  <v-icon color="primary" class="mr-2" large>edit</v-icon>
                  <span class="title">Asignar Empleado para Embarque</span>
                </div>

                <!-- Información del Administrador Invitante (Read-only) -->
                <v-card class="mb-6 elevation-0" style="background: #f5f5f5; border-left: 4px solid #1976d2;">
                  <v-card-text class="pa-4">
                    <p class="caption mb-1 text-muted">INFORMACIÓN DEL ADMINISTRADOR INVITANTE</p>
                    <p class="mb-2"><strong>Empresa:</strong> {{ administrador.empresa_nombre }}</p>
                    <p class="mb-0"><strong>Email:</strong> {{ administrador.email }}</p>
                  </v-card-text>
                </v-card>

                <!-- Información del Embarque -->
                <v-card v-if="shipmentInfo" class="mb-6 elevation-0 border-left-primary">
                  <v-card-text class="pa-4">
                    <p class="caption mb-2 text-muted font-weight-bold">DETALLES DEL EMBARQUE</p>
                    <p class="mb-2"><strong>Empresa Origen:</strong> {{ shipmentInfo.company_name }}</p>
                    <p class="mb-2"><strong>Destino:</strong> {{ shipmentInfo.destination }}</p>
                    <p class="mb-2"><strong>Fecha:</strong> {{ formatDate(shipmentInfo.date) }}</p>
                    <p class="mb-2"><strong>Hora:</strong> {{ shipmentInfo.time }}</p>
                    <p v-if="shipmentInfo.loading_area" class="mb-0"><strong>Rampa/Andén:</strong> {{ shipmentInfo.loading_area }}</p>
                  </v-card-text>
                </v-card>

                <!-- Formulario de Datos del Empleado -->
                <div class="mb-4">
                  <p class="subtitle-2 font-weight-bold mb-3 primary--text">DATOS DEL EMPLEADO ASIGNADO</p>
                </div>

                <v-form ref="formStep1" @submit.prevent="submitStep1">
                  <v-text-field
                    v-model="form.contact_name"
                    label="Nombre del Empleado *"
                    outlined
                    dense
                    class="mb-4"
                    prepend-icon="person"
                    :error-messages="errors.contact_name"
                    @input="clearError('contact_name')"
                    maxlength="200"
                    counter="200"
                  ></v-text-field>

                  <div class="mb-4">
                    <label class="caption">Teléfono (mínimo 10 dígitos) *</label>
                    <vue-tel-input
                      v-model="form.phone"
                      @onInput="handlePhoneInput"
                      class="mt-1"
                      placeholder="+1 (555) 123-4567"
                    ></vue-tel-input>
                    <div v-if="errors.phone" class="error-messages mt-1">
                      <span class="caption" style="color: #f44336;">{{ Array.isArray(errors.phone) ? errors.phone[0] : errors.phone }}</span>
                    </div>
                  </div>

                  <v-text-field
                    v-model="form.email"
                    label="Email del Empleado *"
                    outlined
                    dense
                    type="email"
                    class="mb-4"
                    prepend-icon="email"
                    :error-messages="errors.email"
                    @input="clearError('email')"
                    placeholder="empleado@empresa.com"
                  ></v-text-field>

                  <!-- Nombre de empresa (hidden, se envía al servidor) -->
                  <input v-model="form.company_name" type="hidden">

                  <v-text-field
                    v-model="form.observations"
                    label="Observaciones (Opcional)"
                    outlined
                    dense
                    class="mb-6"
                    prepend-icon="note"
                    placeholder="Ej: Disponible solo mañana"
                    maxlength="500"
                    counter="500"
                  ></v-text-field>

                  <div class="d-flex gap-2">
                    <v-btn
                      color="primary"
                      large
                      depressed
                      type="submit"
                      :loading="loading"
                      block
                    >
                      Siguiente <v-icon right>arrow_forward</v-icon>
                    </v-btn>
                  </div>
                </v-form>
              </div>

              <!-- Paso 2: Documentos (INE frente, INE atrás y foto de perfil - obligatorios) -->
              <div v-if="currentStep === 2 && shipmentInfo" class="step-container">
                <div class="step-title mb-6">
                  <v-icon color="primary" class="mr-2" large>attach_file</v-icon>
                  <span class="title">Cargar Documentos Requeridos</span>
                </div>

                <v-alert type="info" outlined dense class="mb-4">
                  Los documentos marcados como obligatorios son requeridos. Formatos permitidos: JPG o PNG, máximo 5MB por archivo.
                </v-alert>

                <div v-for="doc in allRequiredDocuments" :key="doc.key" class="document-upload-section mb-6">
                  <v-card class="elevation-0 border">
                    <v-card-text class="pa-4">
                      <!-- Encabezado del Documento -->
                      <div class="d-flex align-start mb-4">
                        <div class="flex-grow-1">
                          <p class="body-2 font-weight-bold mb-1">{{ doc.label }}</p>
                          <p class="caption text-muted mb-2">{{ doc.description }}</p>
                        </div>
                        <v-chip
                          v-if="files[doc.key]"
                          small
                          color="success"
                          text-color="white"
                        >
                          <v-icon left small>check_circle</v-icon>
                          Cargado
                        </v-chip>
                        <v-chip
                          v-else-if="doc.obligatorio"
                          small
                          color="error"
                          text-color="white"
                        >
                          Obligatorio
                        </v-chip>
                        <v-chip
                          v-else
                          small
                          color="grey"
                          text-color="white"
                        >
                          Opcional
                        </v-chip>
                      </div>

                      <!-- Preview cuando ya hay archivo -->
                      <div v-if="filePreviews[doc.key]" class="text-center mb-3">
                        <img :src="filePreviews[doc.key]" class="doc-preview" :alt="doc.label">
                      </div>

                      <!-- Area de Drag-Drop -->
                      <div
                        v-else
                        @click="triggerFileInput(doc.key)"
                        @dragover.prevent="isDragging = true"
                        @dragleave.prevent="isDragging = false"
                        @drop.prevent="handleFileDrop($event, doc.key)"
                        :class="['drag-drop-zone', { 'dragging': isDragging }]"
                      >
                        <v-icon size="40" color="primary" class="mb-2">cloud_upload</v-icon>
                        <p class="subtitle-2 mb-1">Arrastra la imagen aquí</p>
                        <p class="caption text-muted mb-2">o haz clic para seleccionar</p>
                        <p class="caption primary--text">JPG o PNG (máximo 5MB)</p>
                      </div>

                      <input
                        :ref="`fileInput_${doc.key}`"
                        type="file"
                        @change="(e) => handleFileSelect(e, doc.key)"
                        accept=".jpg,.jpeg,.png,image/jpeg,image/png"
                        style="display: none"
                      />

                      <!-- Error del documento -->
                      <v-alert v-if="errors[doc.key]" type="error" class="mt-4" outlined dense>
                        {{ Array.isArray(errors[doc.key]) ? errors[doc.key][0] : errors[doc.key] }}
                      </v-alert>

                      <!-- Archivo cargado -->
                      <div v-if="files[doc.key]" class="mt-4 text-center">
                        <div class="file-item justify-center">
                          <v-chip
                            color="success"
                            text-color="white"
                            small
                            class="mr-2"
                          >
                            <v-icon left small>check_circle</v-icon>
                            {{ files[doc.key].name }}
                          </v-chip>
                          <v-btn
                            icon
                            x-small
                            @click="removeFile(doc.key)"
                          >
                            <v-icon x-small>close</v-icon>
                          </v-btn>
                        </div>
                      </div>
                    </v-card-text>
                  </v-card>
                </div>

                <div class="d-flex gap-2 mt-6">
                  <v-btn
                    color="default"
                    large
                    depressed
                    @click="goToStep(1)"
                    :disabled="loading"
                  >
                    <v-icon left>arrow_back</v-icon>
                    Atrás
                  </v-btn>
                  <v-spacer></v-spacer>
                  <v-btn
                    color="primary"
                    large
                    depressed
                    @click="submitStep2"
                    :loading="loading"
                  >
                    Siguiente
                    <v-icon right>arrow_forward</v-icon>
                  </v-btn>
                </div>
              </div>

              <!-- Paso 3: Confirmación -->
              <div v-if="currentStep === 3 && shipmentInfo" class="step-container">
                <div class="step-title mb-6">
                  <v-icon color="primary" class="mr-2" large>verified_user</v-icon>
                  <span class="title">Confirmar Datos</span>
                </div>

                <!-- Resumen -->
                <v-card class="mb-6 elevation-0 border">
                  <v-card-text class="pa-4">
                    <div class="summary-item mb-3">
                      <span class="caption text-muted">Nombre:</span>
                      <p class="body-2 mb-0">{{ form.contact_name }}</p>
                    </div>
                    <div class="summary-item mb-3">
                      <span class="caption text-muted">Teléfono:</span>
                      <p class="body-2 mb-0">{{ form.phone }}</p>
                    </div>
                    <div class="summary-item mb-3">
                      <span class="caption text-muted">Empresa:</span>
                      <p class="body-2 mb-0">{{ form.company_name }}</p>
                    </div>
                    <div class="summary-item mb-3">
                      <span class="caption text-muted">Email:</span>
                      <p class="body-2 mb-0">{{ form.email }}</p>
                    </div>
                    <div v-if="form.observations" class="summary-item mb-3">
                      <span class="caption text-muted">Observaciones:</span>
                      <p class="body-2 mb-0">{{ form.observations }}</p>
                    </div>

                    <div class="summary-item">
                      <span class="caption text-muted">Documentos Cargados:</span>
                      <div v-for="doc in allRequiredDocuments" :key="doc.key" class="mt-1">
                        <p class="body-2 mb-0">✓ {{ doc.label }}: {{ files[doc.key] ? files[doc.key].name : 'Faltante' }}</p>
                      </div>
                    </div>
                  </v-card-text>
                </v-card>

                <!-- Checkbox de Consentimiento -->
                <v-checkbox
                  v-model="consent"
                  class="mb-6"
                  :error-messages="errors.consent"
                  @change="clearError('consent')"
                >
                  <template v-slot:label>
                    <span class="caption">Confirmo que los datos son correctos y autorizo su procesamiento *</span>
                  </template>
                </v-checkbox>

                <!-- Errores Generales -->
                <v-alert v-if="errors.general" type="error" class="mb-4" outlined>
                  {{ errors.general }}
                </v-alert>

                <div class="d-flex gap-2">
                  <v-btn
                    color="default"
                    large
                    depressed
                    @click="goToStep(2)"
                    :disabled="loading"
                  >
                    <v-icon left>arrow_back</v-icon>
                    Atrás
                  </v-btn>
                  <v-spacer></v-spacer>
                  <v-btn
                    color="success"
                    large
                    depressed
                    @click="submitStep3"
                    :loading="loading"
                    :disabled="!consent"
                  >
                    Completar y Generar QR
                    <v-icon right>check_circle</v-icon>
                  </v-btn>
                </div>
              </div>

              <!-- Paso 4: Éxito -->
              <div v-if="currentStep === 4 && qrCode" class="step-container text-center">
                <v-icon size="80" color="success" class="mb-4">check_circle</v-icon>
                <h2 class="headline font-weight-bold mb-2">¡Onboarding Completado!</h2>

                <v-alert type="success" class="mb-6" outlined icon="info">
                  <strong>Tu solicitud ha sido procesada exitosamente.</strong><br>
                  Tu código QR ha sido enviado a <strong>{{ form.email }}</strong><br>
                  <span class="caption">Verifica tu bandeja de entrada (y spam si es necesario)</span>
                </v-alert>

                <v-alert type="warning" class="mb-6" outlined icon="warning">
                  <strong>Acceso de Un Solo Uso</strong><br>
                  <span class="caption">Este link ya no será válido después de cerrar esta página. Si necesitas acceder de nuevo, solicita un nuevo link al administrador.</span>
                </v-alert>

                <v-card class="mb-6 elevation-0 border primary-border">
                  <v-card-text class="pa-6 text-center">
                    <p class="caption text-muted mb-4">Tu Código QR:</p>

                    <!-- QR Image -->
                    <div class="mb-6">
                      <img
                        :src="`https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(qrCode)}&ecc=M&margin=20&size=400x400`"
                        alt="QR Code"
                        class="qr-image"
                      >
                    </div>

                    <!-- QR Code Text -->
                    <p class="caption text-muted mb-4">Código: {{ qrCode }}</p>

                    <!-- Buttons -->
                    <div class="d-flex gap-2 justify-center flex-wrap">
                      <v-btn
                        small
                        outlined
                        color="primary"
                        @click="copyQRToClipboard"
                      >
                        <v-icon left small>content_copy</v-icon>
                        Copiar Código
                      </v-btn>
                      <v-btn
                        small
                        :outlined="!qrDownloaded"
                        :color="qrDownloaded ? 'success' : 'primary'"
                        @click="downloadQR"
                        :disabled="qrDownloaded"
                      >
                        <v-icon left small>{{ qrDownloaded ? 'check_circle' : 'download' }}</v-icon>
                        {{ qrDownloaded ? 'QR Descargado' : 'Descargar QR' }}
                      </v-btn>
                    </div>
                  </v-card-text>
                </v-card>

                <div class="info-box pa-4 mb-6 elevation-0 border">
                  <p class="subtitle-2 font-weight-bold mb-3">Próximos Pasos:</p>
                  <ul class="text-left">
                    <li class="mb-2">✓ Guarda tu código QR</li>
                    <li class="mb-2">✓ Presenta el QR al llegar a la rampa</li>
                    <li class="mb-2">✓ Lleva identificación válida</li>
                    <li>✓ Cumple con la hora programada</li>
                  </ul>
                </div>

                <p class="caption text-muted">¡Gracias por usar nuestro servicio!</p>
              </div>

              <!-- Errores Generales -->
              <v-alert v-if="showError" type="error" class="mt-4" closable @input="showError = false">
                {{ errorMessage }}
              </v-alert>
            </v-card-text>
          </v-card>
        </v-flex>
      </v-layout>
    </v-container>

    <!-- Snackbar -->
    <v-snackbar v-model="showSnackbar" :color="snackbarColor" bottom right>
      {{ snackbarMessage }}
      <template v-slot:action="{ attrs }">
        <v-btn color="white" text small v-bind="attrs" @click="showSnackbar = false">
          Cerrar
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'ExternalOnboarding',
  data() {
    return {
      token: null,
      currentStep: 1,
      loading: false,
      isDragging: false,

      shipmentInfo: null,
      administrador: {
        email: '',
        empresa_nombre: ''
      },

      // Documentos obligatorios fijos requeridos por POST /complete/
      requiredDocuments: [
        { key: 'ine_frente', label: 'INE - Frente', description: 'Identificación oficial, lado frontal' },
        { key: 'ine_atras', label: 'INE - Reverso', description: 'Identificación oficial, lado posterior' },
        { key: 'avatar', label: 'Foto de perfil', description: 'Fotografía reciente de tu rostro' }
      ],

      // Documentos dinámicos requeridos por el administrador (vienen del GET /form/)
      documentosRequeridos: [],
      requiereDocumentos: false,

      form: {
        contact_name: '',
        phone: '',
        email: '',
        observations: '',
        company_name: ''
      },

      files: {
        ine_frente: null,
        ine_atras: null,
        avatar: null
      },
      filePreviews: {
        ine_frente: null,
        ine_atras: null,
        avatar: null
      },

      consent: false,
      qrCode: '',
      qrDownloaded: false,

      errors: {},
      showError: false,
      errorMessage: '',
      showSnackbar: false,
      snackbarMessage: '',
      snackbarColor: 'success'
    }
  },
  computed: {
    progressPercentage() {
      const percentages = {
        1: 25,
        2: 50,
        3: 75,
        4: 100
      }
      return percentages[this.currentStep] || 0
    },
    progressText() {
      const texts = {
        1: 'Paso 1: Información de Contacto (25%)',
        2: 'Paso 2: Documentos (50%)',
        3: 'Paso 3: Confirmación (75%)',
        4: '¡Completado! (100%)'
      }
      return texts[this.currentStep] || ''
    },
    // Une los 3 documentos fijos (INE frente/reverso, avatar) con los documentos
    // dinámicos que pida el administrador (RFC, CURP, etc.) para mostrarlos en un solo listado
    allRequiredDocuments() {
      const fixed = this.requiredDocuments.map(doc => ({ ...doc, obligatorio: true, dynamic: false }))
      const dynamic = this.documentosRequeridos.map(doc => ({
        key: `dyn_${doc.id}`,
        id: doc.id,
        label: doc.nombre,
        description: doc.descripcion,
        obligatorio: doc.obligatorio,
        dynamic: true
      }))
      return [...fixed, ...dynamic]
    }
  },
  mounted() {
    this.token = this.$route.params.token
    if (!this.token) {
      this.showErrorMessage('Token no válido')
      return
    }
    this.loadShipmentInfo()
  },
  methods: {
    async loadShipmentInfo() {
      try {
        const response = await axios.get(
          `https://empresa-service.intrare.app/shipments/advanced/external-onboarding/${this.token}/form/`,
          { timeout: 5000 }
        )

        if (response.data) {
          // Mapear datos del embarque
          if (response.data.embarque) {
            this.shipmentInfo = {
              company_name: response.data.embarque.empresa_origen,
              destination: response.data.embarque.empresa_destino,
              date: response.data.embarque.fecha,
              time: response.data.embarque.hora,
              loading_area: response.data.embarque.rampa
            }
          }

          // Mapear datos del administrador
          if (response.data.administrador) {
            this.administrador = response.data.administrador
            // Prellenar company_name (necesario para el backend)
            if (response.data.administrador.empresa_nombre) {
              this.form.company_name = response.data.administrador.empresa_nombre
            }
          }

          // Mapear documentos dinámicos requeridos (RFC, CURP, etc.)
          if (response.data.documentos_requeridos && Array.isArray(response.data.documentos_requeridos)) {
            this.documentosRequeridos = response.data.documentos_requeridos
          }
          this.requiereDocumentos = !!response.data.requiere_documentos
        }
      } catch (error) {
        if (error.response && error.response.status === 400) {
          if (error.response.data && error.response.data.error) {
            if (error.response.data.error.includes('ya fue utilizado')) {
              this.showErrorMessage('Este link ya fue utilizado. El onboarding se completó correctamente. Revisa tu correo para más información.')
            } else {
              this.showErrorMessage('Error: ' + error.response.data.error)
            }
          } else {
            this.showErrorMessage('El link de acceso no es válido')
          }
        } else if (error.response && error.response.status === 401) {
          this.showErrorMessage('El link de acceso no es válido')
        } else if (error.response && error.response.status === 403) {
          this.showErrorMessage('El link de acceso ha expirado o ya fue utilizado')
        } else {
          this.showErrorMessage('Error al cargar información. Por favor intenta más tarde.')
        }
      }
    },
    submitStep1() {
      this.errors = {}

      // Validaciones en cliente
      if (!this.form.contact_name || this.form.contact_name.length < 3) {
        this.errors.contact_name = 'El nombre debe tener al menos 3 caracteres'
      }
      if (!this.form.phone || this.form.phone.replace(/\D/g, '').length < 10) {
        this.errors.phone = 'El teléfono debe tener al menos 10 dígitos'
      }
      if (!this.form.email || !this.isValidEmail(this.form.email)) {
        this.errors.email = 'Email no válido'
      }

      if (Object.keys(this.errors).length > 0) {
        return
      }

      this.goToStep(2)
      this.showSnackbarMessage('Datos validados correctamente', 'success')
    },
    async submitStep2() {
      // Validar que todos los documentos obligatorios (fijos + dinámicos) estén cargados
      const faltantes = this.allRequiredDocuments.filter(doc => doc.obligatorio && !this.files[doc.key])
      if (faltantes.length > 0) {
        this.showSnackbarMessage('Por favor carga todos los documentos obligatorios', 'error')
        return
      }

      this.loading = true
      try {
        // POST /submit-form/ con los datos básicos (preliminar, antes de /complete/)
        await axios.post(
          `https://empresa-service.intrare.app/shipments/advanced/external-onboarding/${this.token}/submit-form/`,
          {
            contact_name: this.form.contact_name,
            phone: this.form.phone,
            email: this.form.email,
            company_name: this.form.company_name,
            observations: this.form.observations
          }
        )

        // POST /upload-dynamic-documents/ con los documentos solicitados por el administrador (RFC, CURP, etc.)
        if (this.documentosRequeridos.length > 0) {
          const dynamicFormData = new FormData()
          this.documentosRequeridos.forEach((doc, index) => {
            const file = this.files[`dyn_${doc.id}`]
            if (file) {
              dynamicFormData.append(`documents[${index}]documentation_id`, doc.id)
              dynamicFormData.append(`documents[${index}]file`, file)
            }
          })
          await axios.post(
            `https://empresa-service.intrare.app/shipments/advanced/external-onboarding/${this.token}/upload-dynamic-documents/`,
            dynamicFormData
          )
        }

        this.goToStep(3)
      } catch (error) {
        if (error.response && error.response.data) {
          this.errors = error.response.data
        }
        this.showSnackbarMessage('Error al guardar los datos', 'error')
      } finally {
        this.loading = false
      }
    },
    async submitStep3() {
      if (!this.consent) {
        this.errors.consent = 'Debe confirmar que los datos son correctos'
        return
      }

      const faltantes = this.requiredDocuments.filter(doc => !this.files[doc.key])
      if (faltantes.length > 0) {
        this.showSnackbarMessage('Faltan documentos obligatorios por cargar', 'error')
        this.goToStep(2)
        return
      }

      this.errors = {}
      this.loading = true

      try {
        const formData = new FormData()
        formData.append('contact_name', this.form.contact_name)
        formData.append('email', this.form.email)
        formData.append('phone', this.form.phone)
        formData.append('company_name', this.form.company_name)
        formData.append('ine_frente', this.files.ine_frente)
        formData.append('ine_atras', this.files.ine_atras)
        formData.append('avatar', this.files.avatar)
        formData.append('consent', this.consent)

        // multipart/form-data: no fijar el header Content-Type manualmente,
        // axios agrega el boundary correcto automáticamente para FormData.
        const response = await axios.post(
          `https://empresa-service.intrare.app/shipments/advanced/external-onboarding/${this.token}/complete/`,
          formData
        )

        if (response.data.success) {
          this.qrCode = response.data.qr_code
          this.goToStep(4)
          this.showSnackbarMessage('¡Onboarding completado! QR enviado a tu email', 'success')
        }
      } catch (error) {
        const data = error.response && error.response.data
        if (data) {
          this.errors = data
          if (data.error) {
            this.errors.general = data.error
          }
          const docKeys = this.requiredDocuments.map(doc => doc.key)
          const hasDocErrors = docKeys.some(key => data[key])
          if (hasDocErrors) {
            this.showSnackbarMessage('Faltan documentos obligatorios', 'error')
            this.goToStep(2)
          } else {
            this.showSnackbarMessage(data.error || 'Error al completar el onboarding', 'error')
          }
        } else {
          this.showSnackbarMessage('Error al completar el onboarding', 'error')
        }
      } finally {
        this.loading = false
      }
    },
    triggerFileInput(key) {
      this.$refs[`fileInput_${key}`][0].click()
    },
    handleFileSelect(event, key) {
      const file = event.target.files[0]
      if (file) {
        this.setFile(file, key)
      }
    },
    handleFileDrop(event, key) {
      this.isDragging = false
      const file = event.dataTransfer.files[0]
      if (file) {
        this.setFile(file, key)
      }
    },
    setFile(file, key) {
      this.$delete(this.errors, key)

      const allowedTypes = ['image/jpeg', 'image/png']
      if (!allowedTypes.includes(file.type)) {
        this.$set(this.errors, key, 'Tipo de archivo no permitido. Usa JPG o PNG')
        return
      }

      const maxSize = 5 * 1024 * 1024
      if (file.size > maxSize) {
        this.$set(this.errors, key, 'El archivo no puede exceder 5MB')
        return
      }

      this.$set(this.files, key, file)

      const reader = new FileReader()
      reader.onload = (e) => {
        this.$set(this.filePreviews, key, e.target.result)
      }
      reader.readAsDataURL(file)

      this.showSnackbarMessage('Documento cargado', 'success')
    },
    removeFile(key) {
      this.$set(this.files, key, null)
      this.$set(this.filePreviews, key, null)
      if (this.$refs[`fileInput_${key}`]) {
        this.$refs[`fileInput_${key}`][0].value = null
      }
    },
    goToStep(step) {
      this.currentStep = step
      this.$nextTick(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' })
      })
    },
    copyQRToClipboard() {
      navigator.clipboard.writeText(this.qrCode)
      this.showSnackbarMessage('Código QR copiado al portapapeles', 'success')
    },
    downloadQR() {
      const qrImageUrl = `https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(this.qrCode)}&ecc=M&margin=20&size=400x400`
      const link = document.createElement('a')
      link.href = qrImageUrl
      link.download = `qr-code-${this.qrCode.substring(0, 10)}.png`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      this.qrDownloaded = true
      this.showSnackbarMessage('QR descargado correctamente', 'success')
    },
    isValidEmail(email) {
      const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      return re.test(email)
    },
    clearError(field) {
      delete this.errors[field]
    },
    showErrorMessage(message) {
      this.showError = true
      this.errorMessage = message
    },
    showSnackbarMessage(message, color = 'success') {
      this.snackbarMessage = message
      this.snackbarColor = color
      this.showSnackbar = true
    },
    formatDate(dateString) {
      if (!dateString) return 'N/A'
      const date = new Date(dateString)
      return date.toLocaleDateString('es-MX', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    },
    handlePhoneInput({ number, isValid, country }) {
      if (number && number.e164) {
        this.form.phone = number.e164
      }
      // Validar que tenga mínimo 10 dígitos
      if (this.form.phone) {
        const digitos = this.form.phone.replace(/\D/g, '')
        if (digitos.length < 10) {
          this.errors.phone = 'El teléfono debe tener al menos 10 dígitos'
        } else {
          this.clearError('phone')
        }
      }
    }
  }
}
</script>

<style scoped>
.external-onboarding {
  background: #f5f5f5;
  min-height: 100vh;
  padding: 40px 0;
}

.gap-2 {
  gap: 8px;
}

.error-messages {
  display: block;
}

.caption {
  font-size: 12px;
}

.mt-1 {
  margin-top: 8px;
}

.qr-image {
  max-width: 100%;
  width: 400px;
  height: 400px;
  border: 2px solid #002b48;
  border-radius: 8px;
  padding: 8px;
  background: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.justify-center {
  justify-content: center;
}

.flex-wrap {
  flex-wrap: wrap;
}

.border {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
}

.border-bottom {
  border-bottom: 1px solid #e0e0e0;
}

.border-left-primary {
  border-left: 4px solid #002b48 !important;
}

.primary-border {
  border: 2px solid #002b48 !important;
}

.step-title {
  display: flex;
  align-items: center;
}

.progress-bar {
  width: 100%;
  height: 8px;
  background: #e0e0e0;
  border-radius: 4px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: #002b48;
  transition: width 0.3s ease;
}

.drag-drop-zone {
  border: 2px dashed #002b48;
  border-radius: 8px;
  padding: 40px 20px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background: #f9f9f9;
}

.drag-drop-zone:hover,
.drag-drop-zone.dragging {
  border-color: #002b48;
  background: #f0f8ff;
  box-shadow: 0 4px 12px rgba(0, 43, 72, 0.1);
}

.doc-preview {
  max-width: 100%;
  max-height: 180px;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
}

.file-item {
  display: flex;
  align-items: center;
  padding: 8px;
  background: #f5f5f5;
  border-radius: 4px;
}

.summary-item {
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 12px;
}

.summary-item:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.info-box {
  background: #f5f5f5;
  border-radius: 4px;
}

.info-box ul {
  list-style: none;
  padding: 0;
}

.info-box li {
  color: #555;
}

.text-muted {
  color: #999;
}

.document-requirement {
  padding: 12px;
  background: #f9f9f9;
  border-radius: 4px;
  border-left: 3px solid #002b48;
}

.d-flex {
  display: flex;
}

.align-start {
  align-items: flex-start;
}

.rounded-top {
  border-radius: 4px 4px 0 0;
}

.step-container {
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
